import type { AdminAuditEntry, AuditSeverity } from '@/api/opsTypes';

/**
 * Readable labels for the services' admin-audit action strings. The raw string stays the source of truth
 * (the action filter matches it, and the page shows it under the label); an action with no label here is
 * shown raw, never hidden or guessed.
 *
 * Billing: `AdminAuditActions` (anyware.billing `Billing.Application/Admin/AdminActor.cs`).
 * Messaging: the `AdminService` / `TemplateService` admin-log writes (anyware.messaging).
 */
export const BILLING_AUDIT_ACTION_LABELS: Readonly<Record<string, string>> = {
  // Console writes
  'account.provisioned': 'Billing account provisioned',
  'settings.updated': 'Billing settings updated',
  'fx_rate.created': 'FX rate added',
  'profile.updated': 'Billing profile updated',
  'cost_card.created': 'Cost card added',
  'pricing_rule.updated': 'Pricing rule updated',
  'price_book.draft_created': 'Price-book draft created',
  'price_book.items_replaced': 'Price-book draft items replaced',
  'price_book.consumption_generated': 'Consumption prices generated from cost × markup',
  'price_book.published': 'Price book published',
  'override.created': 'Price override created',
  'override.expired': 'Price override expired',
  'offer.created': 'Offer created',
  'offer.expired': 'Offer expired',
  'retention_policy.updated': 'Retention policy updated',
  'retention_policy.enforcement_enabled': 'Retention purging enabled',
  'retention_policy.enforcement_disabled': 'Retention purging disabled',
  'retention_override.set': 'Tenant retention override set',
  'retention_override.deleted': 'Tenant retention override removed',
  'retention.purged': 'Retention purge run',
  'api_key.created': 'API key created',
  'api_key.rotated': 'API key rotated',
  'api_key.revoked': 'API key revoked',
  'gateway.created': 'Payment gateway created',
  'gateway.updated': 'Payment gateway updated',
  'gateway.enabled': 'Payment gateway enabled',
  'gateway.disabled': 'Payment gateway disabled',
  'gateway.retired': 'Payment gateway retired',
  'gateway.tested': 'Payment gateway tested',
  'gateway.seeded': 'Payment gateway seeded from configuration',
  'grant.manual': 'Manual credit grant',
  'grant.retried': 'Credit grant retried',
  'purchase.created': 'Credit purchase created',
  'invoices.generated': 'Invoice run',
  'invoice.issued': 'Invoice issued',
  'invoice.voided': 'Invoice voided',
  'invoice.marked_paid': 'Invoice marked paid',
  'ext_webhook.updated': 'External webhook endpoint updated',
  // Automated money transitions (System actor)
  'purchase.paid': 'Credit purchase paid',
  'invoice.paid': 'Invoice paid',
  'ext_intent.succeeded': 'External payment succeeded',
  'ext_intent.failed': 'External payment failed',
  'ext_intent.refunded': 'External payment refunded',
  'grant.applied': 'Credits granted to the wallet',
  'grant.parked': 'Credit grant parked — needs a retry',
  'invoice.overdue': 'Invoice overdue',
  'subscription.status_reported': 'Subscription status reported to IAM',
  'subscription.price_written_back': 'Plan price written back to IAM',
  'invoice.generated': 'Invoice generated',
  'invoice.payment_link_created': 'Invoice payment link created',
};

export const MESSAGING_AUDIT_ACTION_LABELS: Readonly<Record<string, string>> = {
  'provider.created': 'SMS provider added',
  'provider.updated': 'SMS provider updated',
  'provider.credentials_updated': 'SMS provider credentials replaced',
  'provider.tested': 'SMS provider tested',
  'route.created': 'Route added',
  'route.updated': 'Route updated',
  'route.deleted': 'Route deleted',
  'compliance_profile.updated': 'Compliance profile updated',
  'legacy_concept.created': 'Legacy concept added',
  'legacy_concept.updated': 'Legacy concept updated',
  'sender_id.created': 'Sender ID created',
  'sender_id.submit': 'Sender ID submitted for registration',
  'sender_id.approve': 'Sender ID approved',
  'sender_id.reject': 'Sender ID rejected',
  'sender_id.suspend': 'Sender ID suspended',
  'provider_payment.recorded': 'Provider payment recorded',
  'wallet.granted': 'Communications credits granted',
  'account.credit_mode_changed': 'Credit mode changed (prepaid / postpaid)',
  'template.registration_changed': 'Template registration changed',
};

export type AuditSource = 'billing' | 'messaging';

/** The label for an action string, looked up in its own service's vocabulary first; null when unknown. */
export function auditActionLabel(action: string, source?: AuditSource): string | null {
  const own = source === 'messaging' ? MESSAGING_AUDIT_ACTION_LABELS : BILLING_AUDIT_ACTION_LABELS;
  const other = source === 'messaging' ? BILLING_AUDIT_ACTION_LABELS : MESSAGING_AUDIT_ACTION_LABELS;
  return own[action] ?? other[action] ?? null;
}

export const AUDIT_SEVERITIES: readonly AuditSeverity[] = ['security', 'info'];

/** A severity filter value from the URL: only the two the API accepts — anything else means "all". */
export function parseAuditSeverity(raw: string | null | undefined): AuditSeverity | null {
  const value = raw?.trim().toLowerCase();
  return value === 'security' || value === 'info' ? value : null;
}

/** Null or absent severity is info (older rows, messaging's log). */
export function isSecurityEntry(entry: Pick<AdminAuditEntry, 'severity'>): boolean {
  return entry.severity === 'security';
}

/** Billing's workers write as `actor_id: "system"` with the worker's name as `actor_name`. */
export function isSystemActor(entry: Pick<AdminAuditEntry, 'actor_id'>): boolean {
  return entry.actor_id === 'system';
}
